import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { supabase } from '../../supabaseClient';
import { nombreSeguroStorage } from '../../lib/nombre-storage';
import { syncIncidentCompliance } from '../../lib/incident-compliance-sync';
import {
  AGENT_CATEGORIES,
  AGENT_PART,
  CONTACT_SOURCE,
  CONTACT_TYPE,
  SUBSTANDARD_CONDITIONS,
  SUBSTANDARD_ACTIONS,
  WORK_FACTORS,
  WORKER_FACTORS,
  BASIC_CAUSES,
  type CatalogOption,
} from '../../lib/cd513-catalog';
import { SEVERITY_LABELS, type Severity } from './IncidentModal';

// ── Types ──────────────────────────────────────────────────────────────────────

interface IncidentInvestigation {
  id: string;
  company_id: string;
  incident_id: string;
  agent_category: string | null;
  agent_detail: string | null;
  agent_part: string | null;
  contact_source: string | null;
  contact_type: string | null;
  substandard_conditions: string[] | null;
  substandard_actions: string[] | null;
  work_factors: string[] | null;
  worker_factors: string[] | null;
  basic_cause_hazard_id: boolean;
  basic_cause_env_surveillance: boolean;
  basic_cause_prior_investigation: boolean;
  basic_cause_ppe: boolean;
  basic_cause_training: boolean;
  basic_cause_operational_control: boolean;
  provincial_unit: string | null;
  report_delivery_date: string | null;
}

type BasicCauseField =
  | 'basic_cause_hazard_id'
  | 'basic_cause_env_surveillance'
  | 'basic_cause_prior_investigation'
  | 'basic_cause_ppe'
  | 'basic_cause_training'
  | 'basic_cause_operational_control';

const BASIC_CAUSE_WARNINGS: Partial<Record<BasicCauseField, string>> = {
  basic_cause_hazard_id: 'Esta causa sugiere revisar la Matriz de Riesgos de este puesto de trabajo (módulo Matriz de Riesgos).',
  basic_cause_env_surveillance: 'Esta causa sugiere reforzar la vigilancia médica de este trabajador (módulo Vigilancia de Salud).',
  basic_cause_ppe: 'Esta causa sugiere revisar la matriz de EPP de este puesto de trabajo (módulo EPP).',
  basic_cause_training: 'Esta causa sugiere programar una capacitación para este trabajador (módulo Capacitaciones).',
  basic_cause_operational_control: 'Esta causa sugiere documentar o actualizar el procedimiento de control (módulo Documentos SSO).',
};

interface Props {
  companyId: string;
  incidentId: string;
  onSaved: () => void;
  onClose: () => void;
}

interface IncidentSummary {
  incident_date: string;
  description: string;
  severity: Severity;
  worker_name: string | null;
}

interface CompanyBasicInfo {
  nombre: string;
  ruc: string | null;
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function IncidentInvestigationModal({ companyId, incidentId, onSaved, onClose }: Props) {
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);

  const [agentCategory, setAgentCategory] = useState('');
  const [agentDetail, setAgentDetail]     = useState('');
  const [agentPart, setAgentPart]         = useState('');
  const [contactSource, setContactSource] = useState('');
  const [contactType, setContactType]     = useState('');

  const [substandardConditions, setSubstandardConditions] = useState<string[]>([]);
  const [substandardActions, setSubstandardActions]       = useState<string[]>([]);

  const [workFactors, setWorkFactors]     = useState<string[]>([]);
  const [workerFactors, setWorkerFactors] = useState<string[]>([]);

  const [basicCauses, setBasicCauses] = useState<Record<BasicCauseField, boolean>>({
    basic_cause_hazard_id: false,
    basic_cause_env_surveillance: false,
    basic_cause_prior_investigation: false,
    basic_cause_ppe: false,
    basic_cause_training: false,
    basic_cause_operational_control: false,
  });

  const [provincialUnit, setProvincialUnit]         = useState('');
  const [reportDeliveryDate, setReportDeliveryDate] = useState('');

  const [saving, setSaving]           = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [incidentInfo, setIncidentInfo]   = useState<IncidentSummary | null>(null);
  const [companyInfo, setCompanyInfo]     = useState<CompanyBasicInfo | null>(null);

  useEffect(() => {
    (async () => {
      const { data: inc } = await supabase
        .from('incidents')
        .select('incident_date, description, severity, worker_id')
        .eq('id', incidentId)
        .single();

      let workerName: string | null = null;
      if (inc?.worker_id) {
        const { data: w } = await supabase
          .from('workers')
          .select('full_name')
          .eq('id', inc.worker_id)
          .single();
        workerName = w?.full_name ?? null;
      }

      if (inc) {
        setIncidentInfo({
          incident_date: inc.incident_date,
          description:   inc.description,
          severity:      inc.severity,
          worker_name:   workerName,
        });
      }

      const { data: comp } = await supabase
        .from('companies')
        .select('name, ruc')
        .eq('id', companyId)
        .single();

      if (comp) {
        setCompanyInfo({ nombre: comp.name, ruc: comp.ruc ?? null });
      }
    })();
  }, [incidentId, companyId]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('incident_investigations')
        .select('*')
        .eq('incident_id', incidentId)
        .maybeSingle();

      if (data) {
        const inv = data as IncidentInvestigation;
        setIsEdit(true);
        setAgentCategory(inv.agent_category ?? '');
        setAgentDetail(inv.agent_detail ?? '');
        setAgentPart(inv.agent_part ?? '');
        setContactSource(inv.contact_source ?? '');
        setContactType(inv.contact_type ?? '');
        setSubstandardConditions(inv.substandard_conditions ?? []);
        setSubstandardActions(inv.substandard_actions ?? []);
        setWorkFactors(inv.work_factors ?? []);
        setWorkerFactors(inv.worker_factors ?? []);
        setBasicCauses({
          basic_cause_hazard_id: inv.basic_cause_hazard_id ?? false,
          basic_cause_env_surveillance: inv.basic_cause_env_surveillance ?? false,
          basic_cause_prior_investigation: inv.basic_cause_prior_investigation ?? false,
          basic_cause_ppe: inv.basic_cause_ppe ?? false,
          basic_cause_training: inv.basic_cause_training ?? false,
          basic_cause_operational_control: inv.basic_cause_operational_control ?? false,
        });
        setProvincialUnit(inv.provincial_unit ?? '');
        setReportDeliveryDate(inv.report_delivery_date ?? '');
      }
      setLoading(false);
    })();
  }, [incidentId]);

  function toggleInArray(arr: string[], setArr: (v: string[]) => void, code: string) {
    setArr(arr.includes(code) ? arr.filter(c => c !== code) : [...arr, code]);
  }

  function buildPayload() {
    return {
      company_id:  companyId,
      incident_id: incidentId,
      agent_category: agentCategory || null,
      agent_detail:   agentDetail.trim() || null,
      agent_part:     agentPart || null,
      contact_source: contactSource || null,
      contact_type:   contactType || null,
      substandard_conditions: substandardConditions,
      substandard_actions:    substandardActions,
      work_factors:   workFactors,
      worker_factors: workerFactors,
      ...basicCauses,
      provincial_unit:      provincialUnit.trim() || null,
      report_delivery_date: reportDeliveryDate || null,
    };
  }

  async function doSave(): Promise<boolean> {
    setSaving(true);

    const { error } = await supabase
      .from('incident_investigations')
      .upsert(buildPayload(), { onConflict: 'incident_id' });

    setSaving(false);

    if (error) {
      alert('Error al guardar la investigación: ' + error.message);
      return false;
    }
    // Lote E: el Checklist (PO-07 a PO-13) se vuelve a calcular también al guardar la investigación (antes solo lo
    // hacía el formulario del incidente).
    await syncIncidentCompliance(companyId).catch(err => console.error('syncIncidentCompliance', err));
    return true;
  }

  async function handleSave() {
    const ok = await doSave();
    if (ok) onSaved();
  }

  async function handleGeneratePdf() {
    if (!incidentInfo || !companyInfo) return;

    const ok = await doSave();
    if (!ok) return;

    setGeneratingPdf(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      await generateInvestigationPDF(buildPayload(), incidentInfo, companyInfo, userData.user?.id ?? null);
    } catch (err) {
      alert('Error al generar el PDF: ' + (err as Error).message);
    }
    setGeneratingPdf(false);
    onSaved();
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 640, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
            {isEdit ? 'Editar investigación CD 513' : 'Investigación formal — CD 513'}
          </h2>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {loading ? (
            <p style={{ color: '#64748b', fontSize: '0.86rem', padding: '20px 0' }}>Cargando…</p>
          ) : (
            <>
              {/* ── 1. Agente y tipo de contacto ─────────────────────────── */}
              <Sect title="Agente y tipo de contacto">
                <Row>
                  <Field label="Agente o elemento material">
                    <select style={sel} value={agentCategory} onChange={e => setAgentCategory(e.target.value)}>
                      <option value="">— Seleccionar —</option>
                      {AGENT_CATEGORIES.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Parte del agente">
                    <select style={sel} value={agentPart} onChange={e => setAgentPart(e.target.value)}>
                      <option value="">— Seleccionar —</option>
                      {AGENT_PART.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                  </Field>
                </Row>
                <Field label="Detalle del agente">
                  <input style={inp} value={agentDetail} onChange={e => setAgentDetail(e.target.value)} placeholder="Ej: Montacargas eléctrico, línea de producción…" />
                </Field>
                <Row>
                  <Field label="Fuente o actividad durante el accidente">
                    <select style={sel} value={contactSource} onChange={e => setContactSource(e.target.value)}>
                      <option value="">— Seleccionar —</option>
                      {CONTACT_SOURCE.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Tipo de contacto">
                    <select style={sel} value={contactType} onChange={e => setContactType(e.target.value)}>
                      <option value="">— Seleccionar —</option>
                      {CONTACT_TYPE.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                  </Field>
                </Row>
              </Sect>

              {/* ── 2. Causas directas ───────────────────────────────────── */}
              <Sect title="Causas directas">
                <Field label="Condiciones subestándar">
                  <CheckboxList
                    options={SUBSTANDARD_CONDITIONS}
                    selected={substandardConditions}
                    onToggle={code => toggleInArray(substandardConditions, setSubstandardConditions, code)}
                  />
                </Field>
                <Field label="Acciones subestándar">
                  <CheckboxList
                    options={SUBSTANDARD_ACTIONS}
                    selected={substandardActions}
                    onToggle={code => toggleInArray(substandardActions, setSubstandardActions, code)}
                  />
                </Field>
              </Sect>

              {/* ── 3. Causas indirectas ─────────────────────────────────── */}
              <Sect title="Causas indirectas">
                <Field label="Factores de trabajo">
                  <CheckboxList
                    options={WORK_FACTORS}
                    selected={workFactors}
                    onToggle={code => toggleInArray(workFactors, setWorkFactors, code)}
                  />
                </Field>
                <Field label="Factores del trabajador">
                  <CheckboxList
                    options={WORKER_FACTORS}
                    selected={workerFactors}
                    onToggle={code => toggleInArray(workerFactors, setWorkerFactors, code)}
                  />
                </Field>
              </Sect>

              {/* ── 4. Causas básicas (Tercer Anexo CD 513) ──────────────── */}
              <Sect title="Causas básicas (Tercer Anexo CD 513)">
                {BASIC_CAUSES.map(bc => {
                  const field = bc.field as BasicCauseField;
                  const warning = BASIC_CAUSE_WARNINGS[field];
                  return (
                    <Field key={bc.field} label={bc.label}>
                      <Toggle
                        label={basicCauses[field] ? 'Sí' : 'No'}
                        value={basicCauses[field]}
                        onChange={v => setBasicCauses(prev => ({ ...prev, [bc.field]: v }))}
                      />
                      {basicCauses[field] === false && warning && (
                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8,
                          padding: '10px 12px', borderRadius: 8,
                          background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
                        }}>
                          <span style={{ flexShrink: 0, fontSize: '0.9rem' }}>⚠️</span>
                          <span style={{ color: '#fbbf24', fontSize: '0.78rem', lineHeight: 1.4 }}>
                            {warning}
                          </span>
                        </div>
                      )}
                    </Field>
                  );
                })}
              </Sect>

              {/* ── 5. Cierre administrativo ──────────────────────────────── */}
              <Sect title="Cierre administrativo">
                <Field label="Unidad provincial">
                  <input style={inp} value={provincialUnit} onChange={e => setProvincialUnit(e.target.value)} placeholder="Ej: Dirección Provincial del Trabajo — Pichincha" />
                </Field>
                <Field label="Fecha de entrega del reporte">
                  <input style={inp} type="date" value={reportDeliveryDate} onChange={e => setReportDeliveryDate(e.target.value)} />
                </Field>
              </Sect>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={cancelBtn}>Cancelar</button>
          <button
            onClick={handleGeneratePdf}
            disabled={saving || loading || generatingPdf || !incidentInfo || !companyInfo}
            style={{ ...pdfBtn, opacity: saving || loading || generatingPdf || !incidentInfo || !companyInfo ? 0.7 : 1 }}
          >
            {generatingPdf ? 'Generando…' : '📄 Generar PDF'}
          </button>
          <button onClick={handleSave} disabled={saving || loading} style={{ ...saveBtn, opacity: saving || loading ? 0.7 : 1 }}>
            {saving ? 'Guardando…' : 'Guardar investigación'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Sect({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 20 }}>
      <p style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12, textTransform: 'uppercase' }}>{title}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
      {error && <span style={{ color: '#f87171', fontSize: '0.74rem' }}>{error}</span>}
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', paddingTop: 4 }} onClick={() => onChange(!value)}>
      <div style={{ width: 38, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative', background: value ? 'rgba(47,191,113,0.25)' : '#334155', border: `2px solid ${value ? '#2FBF71' : '#475569'}`, transition: 'all 0.2s' }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', position: 'absolute', top: 2, left: value ? 20 : 2, transition: 'left 0.2s', background: value ? '#2FBF71' : '#64748b' }} />
      </div>
      <span style={{ color: '#cbd5e1', fontSize: '0.84rem' }}>{label}</span>
    </div>
  );
}

function CheckboxList({ options, selected, onToggle }: {
  options: { code: string; label: string }[];
  selected: string[];
  onToggle: (code: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map(o => {
        const checked = selected.includes(o.code);
        return (
          <label key={o.code} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(o.code)}
              style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
            />
            <span style={{ color: '#cbd5e1', fontSize: '0.82rem', lineHeight: 1.4 }}>{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel: React.CSSProperties = { ...inp, cursor: 'pointer' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn: React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn: React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const pdfBtn: React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };

// ── PDF Generation ─────────────────────────────────────────────────────────────

interface InvestigationPdfData {
  company_id: string;
  incident_id: string;
  agent_category: string | null;
  agent_detail: string | null;
  agent_part: string | null;
  contact_source: string | null;
  contact_type: string | null;
  substandard_conditions: string[];
  substandard_actions: string[];
  work_factors: string[];
  worker_factors: string[];
  basic_cause_hazard_id: boolean;
  basic_cause_env_surveillance: boolean;
  basic_cause_prior_investigation: boolean;
  basic_cause_ppe: boolean;
  basic_cause_training: boolean;
  basic_cause_operational_control: boolean;
  provincial_unit: string | null;
  report_delivery_date: string | null;
}

function labelFor(list: CatalogOption[], code: string | null): string {
  if (!code) return '—';
  return list.find(o => o.code === code)?.label ?? code;
}

async function generateInvestigationPDF(
  investigation: InvestigationPdfData,
  incident: IncidentSummary,
  company: CompanyBasicInfo,
  uploadedBy: string | null,
): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PW     = 210;
  const PH     = 297;
  const M      = 25;
  const CW     = PW - M * 2;
  const BOTTOM = PH - 18;

  let y = M;

  const lh = (fs: number) => fs * 0.42;

  const checkBreak = (needed: number) => {
    if (y + needed > BOTTOM) {
      doc.addPage();
      y = M;
    }
  };

  const txt = (
    str: string,
    fs: number,
    style: 'normal' | 'bold' | 'italic' = 'normal',
    align: 'left' | 'center' | 'right'  = 'left',
    indent = 0,
    gap    = 3,
  ) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(fs);
    doc.setTextColor(0, 0, 0);
    const xPos  = align === 'center' ? PW / 2 : align === 'right' ? PW - M : M + indent;
    const maxW  = align === 'center' ? CW : CW - indent;
    const lines = doc.splitTextToSize(str, maxW);
    const h     = lines.length * lh(fs);
    checkBreak(h + gap);
    doc.text(lines, xPos, y, { align });
    y += h + gap;
  };

  const skip  = (mm: number) => { y += mm; };
  const hline = () => { doc.setDrawColor(0); doc.line(M, y, PW - M, y); skip(4); };

  const sectionTitle = (title: string) => {
    checkBreak(lh(12) + 8);
    txt(title, 12, 'bold', 'left', 0, 2);
    hline();
  };

  const bull = (t: string) => txt(t, 10, 'normal', 'left', 5, 2);

  // ── 1. Encabezado ──────────────────────────────────────────────────────────
  txt('INFORME DE INVESTIGACIÓN DE ACCIDENTE DE TRABAJO — CD 513', 14, 'bold', 'center', 0, 4);
  txt(company.nombre.toUpperCase(), 12, 'bold', 'center', 0, 3);
  if (company.ruc) txt(`RUC: ${company.ruc}`, 10, 'normal', 'center', 0, 3);
  skip(4);
  hline();

  // ── 2. Datos del accidente ────────────────────────────────────────────────
  sectionTitle('DATOS DEL ACCIDENTE');
  txt(`Fecha: ${fmtDate(incident.incident_date)}`, 10);
  txt(`Trabajador: ${incident.worker_name ?? '—'}`, 10);
  txt(`Gravedad: ${SEVERITY_LABELS[incident.severity]}`, 10);
  txt(`Descripción: ${incident.description}`, 10, 'normal', 'left', 0, 6);

  // ── 3. Agente y tipo de contacto ──────────────────────────────────────────
  sectionTitle('AGENTE Y TIPO DE CONTACTO');
  txt(`Agente o elemento material: ${labelFor(AGENT_CATEGORIES, investigation.agent_category)}`, 10);
  txt(`Detalle del agente: ${investigation.agent_detail || '—'}`, 10);
  txt(`Parte del agente: ${labelFor(AGENT_PART, investigation.agent_part)}`, 10);
  txt(`Fuente o actividad durante el accidente: ${labelFor(CONTACT_SOURCE, investigation.contact_source)}`, 10);
  txt(`Tipo de contacto: ${labelFor(CONTACT_TYPE, investigation.contact_type)}`, 10, 'normal', 'left', 0, 6);

  // ── 4. Causas directas ────────────────────────────────────────────────────
  sectionTitle('CAUSAS DIRECTAS');
  txt('Condiciones subestándar:', 10, 'bold', 'left', 0, 2);
  if (investigation.substandard_conditions.length === 0) {
    txt('— Ninguna registrada', 10, 'italic', 'left', 5, 3);
  } else {
    investigation.substandard_conditions.forEach(code => bull(`• ${labelFor(SUBSTANDARD_CONDITIONS, code)}`));
  }
  skip(2);
  txt('Acciones subestándar:', 10, 'bold', 'left', 0, 2);
  if (investigation.substandard_actions.length === 0) {
    txt('— Ninguna registrada', 10, 'italic', 'left', 5, 3);
  } else {
    investigation.substandard_actions.forEach(code => bull(`• ${labelFor(SUBSTANDARD_ACTIONS, code)}`));
  }
  skip(4);

  // ── 5. Causas indirectas ──────────────────────────────────────────────────
  sectionTitle('CAUSAS INDIRECTAS');
  txt('Factores de trabajo:', 10, 'bold', 'left', 0, 2);
  if (investigation.work_factors.length === 0) {
    txt('— Ninguno registrado', 10, 'italic', 'left', 5, 3);
  } else {
    investigation.work_factors.forEach(code => bull(`• ${labelFor(WORK_FACTORS, code)}`));
  }
  skip(2);
  txt('Factores del trabajador:', 10, 'bold', 'left', 0, 2);
  if (investigation.worker_factors.length === 0) {
    txt('— Ninguno registrado', 10, 'italic', 'left', 5, 3);
  } else {
    investigation.worker_factors.forEach(code => bull(`• ${labelFor(WORKER_FACTORS, code)}`));
  }
  skip(4);

  // ── 6. Causas básicas ─────────────────────────────────────────────────────
  sectionTitle('CAUSAS BÁSICAS (TERCER ANEXO CD 513)');
  BASIC_CAUSES.forEach(bc => {
    const value = investigation[bc.field as keyof InvestigationPdfData] as boolean;
    txt(`${value ? 'Sí' : 'No'}  —  ${bc.label}`, 10, 'normal', 'left', 0, 3);
  });
  skip(4);

  // ── 7. Cierre administrativo ──────────────────────────────────────────────
  sectionTitle('CIERRE ADMINISTRATIVO');
  txt(`Unidad provincial: ${investigation.provincial_unit || '—'}`, 10);
  txt(`Fecha de entrega del reporte: ${investigation.report_delivery_date ? fmtDate(investigation.report_delivery_date) : '—'}`, 10, 'normal', 'left', 0, 8);

  // ── 8. Pie de página — fecha de generación ────────────────────────────────
  const now  = new Date();
  const pad  = (n: number) => String(n).padStart(2, '0');
  const nowS = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  txt(`Documento generado el ${nowS} por StorSafe — storsafe.app`, 9, 'italic', 'center', 0, 0);

  // ── Numeración de páginas (segundo pase) ──────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Página ${p} de ${totalPages}`, PW / 2, PH - 10, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  // ── DESCARGAR ──────────────────────────────────────────────────────────────
  const filename = `Investigacion_CD513_${company.nombre.replace(/[^a-zA-Z0-9]/g, '_')}_${incident.incident_date}.pdf`;
  const blob = doc.output('blob');
  doc.save(filename);

  try {
    const path = `${investigation.company_id}/incident-investigations/${nombreSeguroStorage(`${investigation.incident_id}.pdf`)}`;
    let upErr = (await supabase.storage.from('sso-documents')
      .upload(path, blob, { upsert: false, contentType: 'application/pdf' })).error;

    if (upErr) {
      const { error: updateErr } = await supabase.storage.from('sso-documents')
        .update(path, blob, { contentType: 'application/pdf' });
      if (updateErr) throw updateErr;
    }

    const { data: pub } = supabase.storage.from('sso-documents').getPublicUrl(path);
    const { error: updErr } = await supabase
      .from('incident_investigations')
      .update({ pdf_url: pub.publicUrl })
      .eq('incident_id', investigation.incident_id);
    if (updErr) throw updErr;

    try {
      const { data: existingDoc } = await supabase
        .from('sso_documents')
        .select('id')
        .eq('company_id', investigation.company_id)
        .in('file_url', [pub.publicUrl, path])
        .maybeSingle();

      if (!existingDoc) {
        const { error: docErr } = await supabase.from('sso_documents').insert({
          company_id:        investigation.company_id,
          category:          'acta',
          title:             `Investigación CD 513 - ${company.nombre} - ${fmtDate(incident.incident_date)}`,
          description:       'Generado automáticamente desde Investigación CD 513',
          file_url:           pub.publicUrl,
          file_name:          `${investigation.incident_id}.pdf`,
          uploaded_by:        uploadedBy,
          is_current:         true,
          checklist_item_id:  'PO-08', // Informe de investigación de accidentes (Resolución IESS CD 513 Art. 47) — coincide semánticamente, no es un código ad-hoc.
        });
        if (docErr) throw docErr;
      }
    } catch (docErr) {
      console.warn('No se pudo registrar el documento en sso_documents:', docErr);
    }
  } catch (err) {
    console.warn('No se pudo persistir pdf_url en incident_investigations:', err);
  }
}
